import Keycloak from 'keycloak-js';
import { resetUser } from 'models/user';

const keycloak = new Keycloak({
	url: process.env.REACT_APP_KC_HOST || '',
	realm: process.env.REACT_APP_KC_REALM || '',
	clientId: process.env.REACT_APP_KC_CLIENT_ID || '',
});

/**
 * URL выхода из Keycloak без id_token_hint (короткий URL).
 * Стандартный keycloak.logout() подставляет в URL полный id_token с группами → 414 Request-URI Too Large при многих группах.
 * Query: client_id, post_logout_redirect_uri. Клиентского параметра для пропуска страницы «Do you want to log out?» нет —
 * отключить её можно только на сервере Keycloak: --spi-login-protocol-openid-connect-suppress-logout-confirmation-screen=true
 */
export function getKeycloakLogoutUrl(
	kc: Keycloak,
	postLogoutRedirectUri: string = `${typeof window !== 'undefined' ? window.location.origin : ''}/login`
): string {
	const base = `${kc.authServerUrl}/realms/${kc.realm}/protocol/openid-connect/logout`;
	const params = new URLSearchParams({
		client_id: kc.clientId || '',
		post_logout_redirect_uri: postLogoutRedirectUri,
	});
	return `${base}?${params.toString()}`;
}

/**
 * Выход из Keycloak через короткий URL (без id_token_hint), чтобы избежать 414 при большом числе групп.
 */
export function keycloakLogoutWithoutIdTokenHint(
	kc: Keycloak,
	postLogoutRedirectUri?: string
): void {
	resetUser();
	window.location.href = getKeycloakLogoutUrl(kc, postLogoutRedirectUri);
}

/** Лимит длины URL выхода: при превышении — 414. Оставляем запас. */
const LOGOUT_URL_SAFE_LENGTH = 6000;

/**
 * Выход: при коротком id_token — keycloak.logout() (без страницы "Do you want to log out?").
 * При большом числе групп id_token длинный → используем короткий URL без hint (иначе 414),
 * и Keycloak показывает кнопку подтверждения. Убрать её можно только на сервере Keycloak:
 * при запуске добавить --spi-login-protocol-openid-connect-suppress-logout-confirmation-screen=true
 */
export function keycloakLogout(
	kc: Keycloak,
	postLogoutRedirectUri?: string
): void {
	const redirectUri = postLogoutRedirectUri ?? `${typeof window !== 'undefined' ? window.location.origin : ''}/login`;
	const idToken = kc.idToken;
	const wouldBeLong =
		!idToken ||
		getKeycloakLogoutUrl(kc, redirectUri).length + idToken.length > LOGOUT_URL_SAFE_LENGTH;

	if (wouldBeLong) {
		// Много групп → короткий URL, иначе 414. Keycloak покажет "Do you want to log out?" — отключить только на сервере (см. комментарий выше).
		keycloakLogoutWithoutIdTokenHint(kc, postLogoutRedirectUri);
		return;
	}
	resetUser();
	kc.logout({ redirectUri });
}

export default keycloak;
